import type {
	API_GetCurrentUserResponse,
	API_UpdateUserRequest,
	API_UpdateUserResponse,
} from "@pilatestime/shared-types";
import { queryOptions, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useMutationWithToast } from "@/hooks/useMutationWithToast";
import api from "./api_client";

// API functions
export const fetchCurrentUser = (
	token: string,
): Promise<API_GetCurrentUserResponse> =>
	api
		.get("/users/me", {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const updateCurrentUser = (
	token: string,
	data: API_UpdateUserRequest,
): Promise<API_UpdateUserResponse> =>
	api
		.put("/users/me", data, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

// Query options
export const fetchCurrentUserQueryOptions = (userId: string, token: string) =>
	queryOptions({
		queryKey: ["current_user", userId],
		queryFn: () => fetchCurrentUser(token),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

// Mutation hooks
export const useUpdateCurrentUser = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			data,
		}: {
			token: string;
			data: API_UpdateUserRequest;
		}) => updateCurrentUser(token, data),
		toastMessages: {
			loading: t("userSettings.toasts.updating"),
			success: t("userSettings.toasts.updateSuccess"),
			error: t("userSettings.toasts.updateError"),
		},
		onSuccess: () => {
			// Invalidate and refetch current user data
			queryClient.invalidateQueries({
				queryKey: ["current_user"],
			});
			// Also invalidate staff_member queries since the user might be viewing their own profile in the staff section
			queryClient.invalidateQueries({
				queryKey: ["staff_member"],
			});
			// Invalidate organization staff lists
			queryClient.invalidateQueries({
				queryKey: ["organization_staff"],
			});
			// Invalidate unified staff and invitations
			queryClient.invalidateQueries({
				queryKey: ["unified_staff_and_invitations"],
			});
		},
	});
};
